/**
 * Physical size measurement from a fixed, calibrated camera.
 *
 * The camera is mounted at a fixed distance/zoom, so a detected object's box occupies a constant
 * fraction of the frame for a real size. Calibrating once (a known object → its box) fixes the
 * cm-per-frame-fraction; every later object's box then converts straight to real cm. Fractions (not
 * raw pixels) are stored so the scale holds regardless of the image resolution the box came from
 * (live stream vs full-res snap, which can differ).
 */

import { CameraCalibration } from '../contexts/settings-context';

/** An object's box and the frame it sits in, all in the same pixel space (unrotated). */
export interface FrameBox {
  /** Object box width (px). */
  w: number;
  /** Object box height (px). */
  h: number;
  /** Frame width (px). */
  frameW: number;
  /** Frame height (px). */
  frameH: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Build a calibration from a reference object's box and its known real size (cm). */
export const calibrationFromBox = (
  box: FrameBox,
  refWidthCm: number,
  refHeightCm: number,
): CameraCalibration | null => {
  if (!box.frameW || !box.frameH || box.w <= 0 || box.h <= 0) return null;
  const fracW = box.w / box.frameW;
  const fracH = box.h / box.frameH;
  if (!fracW || !fracH) return null;
  return {
    cmPerFrameW: refWidthCm / fracW,
    cmPerFrameH: refHeightCm / fracH,
    refWidthCm,
    refHeightCm,
  };
};

/** Measure an object's real size (cm, 1 decimal) from its box using a camera's calibration. */
export const measureCm = (
  box: FrameBox,
  cal: CameraCalibration,
): { widthCm: number; heightCm: number } | null => {
  if (!box.frameW || !box.frameH || box.w <= 0 || box.h <= 0) return null;
  return {
    widthCm: round1((box.w / box.frameW) * cal.cmPerFrameW),
    heightCm: round1((box.h / box.frameH) * cal.cmPerFrameH),
  };
};
